import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { usePropertyEditMutation } from '../hooks/usePropertyEditMutation';
import {
  type SetListingCriteriaMutationVariables,
  useSetListingCriteriaMutation,
} from '../hooks/useSetListingCriteriaMutation';

import TimeConstants from '../constants/TimeConstants';

import { useFormMode } from './FormModeContext';

import type { CriterionProps } from '../model/ListingCriteria/Criterion';

type PendingBatch = {
  listingAlias: string;
  criteria: Map<string, CriterionProps>;
  resolvers: Array<() => void>;
};

export type CriteriaSaveBatchValue = {
  enqueue: (variables: SetListingCriteriaMutationVariables) => Promise<void>;
  flush: () => Promise<void>;
  isPending: boolean;
  error: unknown;
};

const CriteriaSaveBatchContext = createContext<CriteriaSaveBatchValue | null>(null);

/**
 * Collects criteria saves from the fields below it and sends them as one `/criteria/set` call.
 *
 * The wizard debounces, because saves arrive one blur or click at a time. Modal mode flushes on the
 * next tick instead, because `triggerAllSaves` enqueues every field in one synchronous pass, so the
 * merge needs no wait and the modal never sits behind a timer.
 */
export const CriteriaSaveBatchProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const formModeContext = useFormMode();
  const listingAlias = router.query.listingAlias as string;

  const mutation = usePropertyEditMutation({
    mutationHook: useSetListingCriteriaMutation,
    successToastMessage: 'Screening criteria updated',
    meta: {
      invalidates: ['users-properties-criteria-get'],
      context: { details: { listingAlias } },
    },
  });

  const pending = useRef<PendingBatch | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isQueued, setIsQueued] = useState(false);

  const latest = useRef(mutation);
  latest.current = mutation;

  const waitMs = formModeContext.mode === 'wizard' ? TimeConstants.SELECTION_DEBOUNCE_TIME_MS : 0;

  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }

    const batch = pending.current;
    pending.current = null;
    setIsQueued(false);

    if (!batch) {
      return;
    }

    try {
      await latest.current.mutateAsync({
        getParams: { listingAlias: batch.listingAlias },
        postData: Array.from(batch.criteria.values()),
      });
    } catch {
      // The failure surfaces through mutation.error, which the fields already read.
    } finally {
      batch.resolvers.forEach((resolve) => resolve());
    }
  }, []);

  const enqueue = useCallback(
    (variables: SetListingCriteriaMutationVariables) => {
      const incomingAlias = variables.getParams.listingAlias;

      if (pending.current && pending.current.listingAlias !== incomingAlias) {
        void flush();
      }

      const batch: PendingBatch = pending.current ?? {
        listingAlias: incomingAlias,
        criteria: new Map(),
        resolvers: [],
      };

      // Keyed by criterion type so a later write to the same type replaces the earlier one.
      variables.postData.forEach((criterion) => batch.criteria.set(criterion.type, criterion));
      pending.current = batch;
      setIsQueued(true);

      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => void flush(), waitMs);

      return new Promise<void>((resolve) => {
        batch.resolvers.push(resolve);
      });
    },
    [flush, waitMs],
  );

  // Flush rather than cancel, so leaving the step or the page never drops a queued edit.
  useEffect(() => {
    const handlePageHide = () => void flush();
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      void flush();
    };
  }, [flush]);

  return (
    <CriteriaSaveBatchContext.Provider
      value={{ enqueue, flush, isPending: isQueued || mutation.isPending, error: mutation.error }}
    >
      {children}
    </CriteriaSaveBatchContext.Provider>
  );
};

export const useOptionalCriteriaSaveBatch = () => useContext(CriteriaSaveBatchContext);
